import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { getEmailTemplates, updateEmailTemplate } from '@/services/api';
import type { EmailTemplate, TemplateType } from '@/types/emailTemplate';

const TEMPLATE_TABS: { type: TemplateType; label: string }[] = [
  { type: 'TicketQaReply', label: 'Ticket Reply' },
  { type: 'LeadQaReply', label: 'Lead Reply' },
  { type: 'MarketingMaterial', label: 'Marketing Material' },
  { type: 'AutoResponse', label: 'Auto-Response' },
  { type: 'PaidOrderConfirmation', label: 'Order Confirmation' },
];

const ORDER_TOKENS = [
  ['{{Preheader}}', 'Hidden inbox preview'],
  ['{{HeroImage}}', 'Approved Cre8or hero artwork'],
  ['{{CustomerFirstName}}', 'Checkout first name'],
  ['{{OrderReference}}', 'Customer-facing order reference'],
  ['{{OrderSummary}}', 'Paid date, total, and delivery summary'],
  ['{{OrderItems}}', 'Purchased SKU item rows'],
  ['{{DesignJourneyImage}}', 'Approved cosmetics design artwork'],
] as const;

function TemplateEditor({
  template,
  onSaved,
}: {
  template: EmailTemplate;
  onSaved: () => void;
}) {
  const [subject, setSubject] = useState(template.subjectTemplate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState(template.bodyHtml);
  const isOrderConfirmation = template.templateType === 'PaidOrderConfirmation';

  const editor = useEditor({
    extensions: [StarterKit, Link.configure({ openOnClick: false })],
    content: template.bodyHtml,
  });

  const handleSave = async () => {
    if (!editor) return;
    setSaving(true);
    setError(null);
    try {
      await updateEmailTemplate(template.templateType, {
        subjectTemplate: subject,
        bodyHtml: isOrderConfirmation ? source : editor.getHTML(),
      });
      onSaved();
    } catch {
      setError('Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-brand-text-secondary mb-1">Subject</label>
        <input
          type="text"
          value={subject}
          onChange={e => setSubject(e.target.value)}
          className="w-full border border-brand-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
        />
      </div>

      {isOrderConfirmation && (
        <div className="rounded-lg border border-brand-border bg-brand-surface-muted p-4">
          <p className="text-sm font-semibold text-brand-text">Protected order fields</p>
          <p className="mt-1 text-xs text-brand-text-secondary">All tokens below are required. The server keeps the current active version if validation fails.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {ORDER_TOKENS.map(([token, description]) => (
              <button key={token} type="button" title={description} onClick={() => setSource(value => `${value}\n${token}`)} className="rounded-full bg-brand-light-purple px-3 py-1 font-mono text-xs text-brand-primary">
                {token}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-brand-text-secondary mb-1">Body (HTML)</label>
        {isOrderConfirmation ? (
          <textarea value={source} onChange={event => setSource(event.target.value)} rows={22} spellCheck={false} className="w-full rounded-lg border border-brand-border p-3 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary" />
        ) : (
          <div className="border border-brand-border rounded-lg min-h-[200px] p-3 prose prose-sm max-w-none focus-within:ring-2 focus-within:ring-brand-primary">
            <EditorContent editor={editor} />
          </div>
        )}
      </div>

      {isOrderConfirmation && (
        <div>
          <p className="mb-1 text-sm font-medium text-brand-text-secondary">Safe representative preview</p>
          <iframe title="Order confirmation template preview" sandbox="" srcDoc={source
            .replaceAll('{{Preheader}}', '')
            .replaceAll('{{HeroImage}}', '<div style="padding:24px;background:#f5f2ea">Approved Cre8or hero image</div>')
            .replaceAll('{{CustomerFirstName}}', 'Avery')
            .replaceAll('{{OrderReference}}', 'CR8-000842')
            .replaceAll('{{OrderSummary}}', '<p><strong>Paid Aug 7, 2026 · USD 1,248.00</strong></p>')
            .replaceAll('{{OrderItems}}', '<p>Moisturizing Face Cream · SKU CR8-SKU-1042 · Qty 48</p>')
            .replaceAll('{{DesignJourneyImage}}', '<div style="padding:24px;background:#f0eaf8">Approved cosmetics design journey image</div>')} className="h-80 w-full rounded-lg border border-brand-border bg-white" />
        </div>
      )}

      {error && <p className="text-red-500 text-sm">{error}</p>}

      <button
        onClick={handleSave}
        disabled={saving}
        className="px-4 py-2 bg-brand-accent text-brand-text-inverse rounded-lg text-sm font-medium hover:bg-brand-accent-hover disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save Template'}
      </button>
    </div>
  );
}

export default function EmailTemplateEditor() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<TemplateType>('TicketQaReply');

  const { data: templates, isLoading } = useQuery<EmailTemplate[]>({
    queryKey: ['email-templates'],
    queryFn: getEmailTemplates,
  });

  const activeTemplate = templates?.find(t => t.templateType === activeTab);

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Email Templates</h1>

      {/* Tabs */}
      <div className="flex border-b border-brand-border mb-6">
        {TEMPLATE_TABS.map(tab => (
          <button
            key={tab.type}
            onClick={() => setActiveTab(tab.type)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.type
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-brand-text-secondary hover:text-brand-text'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-brand-text-secondary">Loading templates…</p>}

      {!isLoading && !activeTemplate && (
        <p className="text-brand-text-secondary text-sm">No active template found for this type.</p>
      )}

      {activeTemplate && (
        <TemplateEditor
          key={activeTemplate.emailTemplateId}
          template={activeTemplate}
          onSaved={() => qc.invalidateQueries({ queryKey: ['email-templates'] })}
        />
      )}
    </div>
  );
}
